// src/pages/SubdecksPage.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Search, X, Settings, GraduationCap, ArrowRight, BookOpen, Trophy, List, Play } from 'lucide-react';
import { ICON_MAP } from '../components/deck_subdeck/DeckIconPicker';
import HeaderPattern, { HEADER_THEMES } from '../components/shared/HeaderPattern';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    DndContext, closestCenter,
    KeyboardSensor, PointerSensor,
    useSensor, useSensors, DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext,
    sortableKeyboardCoordinates, rectSortingStrategy
} from '@dnd-kit/sortable';
import { useAuth } from '../contexts/AuthContext';
import { exportImportService } from '../services/exportImport.service';
import ConfirmDialog from '../components/ConfirmDialog';
import SubdeckCard from '../components/deck_subdeck/SubdeckCard';

import SubdeckGroupContainer from '../components/deck_subdeck/SubdeckGroupContainer';
import { SubdeckGroup } from '../components/deck_subdeck/SubdeckGroupContainer';
import AssignGroupModal from '../components/deck_subdeck/AssignGroupModal';
import ManageGroupsModal from '../components/deck_subdeck/ManageGroupsModal';
import CreateSubdeckModal from '../components/deck_subdeck/CreateSubdeckModal';
import CustomStudyModal from '../components/deck_subdeck/CustomStudyModal';
import Toast from '../components/Toast';
import StudyModeModal from '../components/study/StudyModeModal';
import ImportModal from '../components/deck_subdeck/ImportModal';
import ImportApostilaModal from '../components/resumo/ImportApostilaModal';
import { resumoService } from '../services/resumo.service';
import InterdictedModal from '../components/AccessControl/InterdictedModal';
import UpgradeModal from '../components/dashboard/UpgradeModal';
import MoveModal from '../components/deck_subdeck/MoveModal';
import CopyModal from '../components/deck_subdeck/CopyModal';

import './SubdecksPage.css';

// ── Types ──────────────────────────────────────────────────────────────
interface Subdeck {
    _id: string;
    name: string;
    description?: string;
    cards: any[];
    cardCount?: number;
    createdAt: string;
    isBlocked?: boolean;
    hasResumo?: boolean;
    isFree?: boolean;
    type?: 'flashcards' | 'apostila';
    groupId?: string | null;
    colorId?: string | null;
    freeConfig?: { enabled: boolean; freeCardsLimit: number; freeApostilaPagesLimit: number; };
}

interface SubdeckStats {
    totalCards: number;
    masteredCards: number;
    masteryPercentage: number;
    lastReviewedAt?: string | null;
}

interface LastStudiedSubdeck {
    subdeckId: string;
    subdeckName: string;
    lastStudiedAt: string;
    masteryPercentage: number;
}

// ── Component ──────────────────────────────────────────────────────────
const SubdecksPage: React.FC = () => {
    const { deckId } = useParams<{ deckId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin, isEditor } = useAuth();

    // Context recovery
    const getContext = () => {
        if (location.state) return location.state as any;
        const saved = sessionStorage.getItem('subdecksContext');
        if (saved) return JSON.parse(saved);
        return { accessMode: 'read', from: 'unknown', returnPath: '/dashboard' };
    };
    const context = getContext();
    const accessMode: 'read' | 'manage' = context.accessMode ?? 'read';
    const canManage = isAdmin || accessMode === 'manage';
    const returnPath = context.returnPath ?? '/dashboard';
    const isPurchasedDeck = isAdmin || (context.purchased === true);

    // ── State ────────────────────────────────────────────────────────
    const [deckName, setDeckName] = useState('');
    const [, setDeckDescription] = useState('');
    const [subdecks, setSubdecks] = useState<Subdeck[]>([]);
    const [totalCards, setTotalCards] = useState(0);
    const [loading, setLoading] = useState(true);
    const [subdeckStats, setSubdeckStats] = useState<Map<string, SubdeckStats>>(new Map());
    const [readStatusMap, setReadStatusMap] = useState<Map<string, boolean>>(new Map());
    const [pageCountMap, setPageCountMap] = useState<Map<string, number>>(new Map());
    const [readPercentageMap, setReadPercentageMap] = useState<Map<string, number>>(new Map());

    // Groups state
    const [groups, setGroups] = useState<SubdeckGroup[]>([]);
    const [groupsLoading, setGroupsLoading] = useState(true);
    const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
        try {
            const saved = localStorage.getItem(`graia_groups_open_${deckId}`);
            if (saved) return new Set(JSON.parse(saved));
        } catch { /* ignore */ }
        return new Set();
    });

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createType, setCreateType] = useState<'flashcards' | 'apostila' | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [subdeckToDelete, setSubdeckToDelete] = useState<string | null>(null);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [importFiles, setImportFiles] = useState<File[]>([]);
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState('');
    const [showCustomStudyModal, setShowCustomStudyModal] = useState(false);
    const [showModeModal, setShowModeModal] = useState(false);
    const [selectedSubdeckForStudy, setSelectedSubdeckForStudy] = useState<any>(null);
    const [showNoResumoModal, setShowNoResumoModal] = useState(false);
    const [selectedSubdeckForModal, setSelectedSubdeckForModal] = useState('');
    const [showInterdictedModal, setShowInterdictedModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
    const [customStudyCache, setCustomStudyCache] = useState<any[] | null>(null);
    const [modeStatsCache, setModeStatsCache] = useState<Map<string, any>>(new Map());

    const statsAbortControllerRef = useRef<AbortController | null>(null);
    const modeStatsCacheRef = useRef<Map<string, any>>(new Map());
    const prefetchModeStatsRef = useRef<(s: Subdeck) => Promise<void>>(() => Promise.resolve());
    const prefetchCustomStudyRef = useRef<() => Promise<void>>(() => Promise.resolve());
    const pageDataCacheRef = useRef<Map<string, any>>(new Map());

    const [showGroupStudyModal, setShowGroupStudyModal] = useState(false);
    const [groupStudyItems, setGroupStudyItems] = useState<any[] | null>(null);
    const [groupStudyName, setGroupStudyName] = useState('');

    const [showMoveModal, setShowMoveModal] = useState(false);
    const [subdeckToMove, setSubdeckToMove] = useState<Subdeck | null>(null);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [subdeckToCopy, setSubdeckToCopy] = useState<Subdeck | null>(null);

    // Assign group modal
    const [showAssignGroupModal, setShowAssignGroupModal] = useState(false);
    const [subdeckForGroup, setSubdeckForGroup] = useState<Subdeck | null>(null);

    // Manage groups modal
    const [showManageGroupsModal, setShowManageGroupsModal] = useState(false);

    // Layout mode
    const [layoutMode, setLayoutMode] = useState<'groups' | 'grid'>('groups');
    const [showLayoutModal, setShowLayoutModal] = useState(false);
    const [savingLayout, setSavingLayout] = useState(false);

    // Import group
    const [importGroupId, setImportGroupId] = useState<string | null>(null);

    // Import apostila
    const [showImportApostilaModal, setShowImportApostilaModal] = useState(false);

    // Header theme, search, admin menu
    const [headerTheme, setHeaderTheme] = useState('default');
    const [searchQuery, setSearchQuery] = useState('');
    const [showAdminMenu, setShowAdminMenu] = useState(false);
    const [lastStudiedSubdeck, setLastStudiedSubdeck] = useState<LastStudiedSubdeck | null>(null);
    const [deckIconId, setDeckIconId] = useState<string | null>(null);
    const [isSkipDeckLevel, setIsSkipDeckLevel] = useState(context.skipDeckLevel === true);

    // Theme picker modal
    const [showThemeModal, setShowThemeModal] = useState(false);
    const [selectedTheme, setSelectedTheme] = useState('default');
    const [themeSaving, setThemeSaving] = useState(false);

    // Ranking modal (todos os subdecks por domínio)
    const [showRankingModal, setShowRankingModal] = useState(false);

    const adminMenuRef = useRef<HTMLDivElement>(null);

    // ── Helpers ───────────────────────────────────────────────────────
    const token = () => localStorage.getItem('accessToken') || '';

    const clearCache = useCallback(() => {
        sessionStorage.removeItem(`subdecks_${deckId}`);
        sessionStorage.removeItem(`groups_${deckId}`);
    }, [deckId]);

    // ── Persist open groups to localStorage ───────────────────────────
    const persistOpenGroups = useCallback((set: Set<string>) => {
        localStorage.setItem(`graia_groups_open_${deckId}`, JSON.stringify(Array.from(set)));
    }, [deckId]);

    const toggleGroup = useCallback((groupId: string) => {
        setOpenGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            persistOpenGroups(next);
            return next;
        });
    }, [persistOpenGroups]);


    // ── Load stats ────────────────────────────────────────────────────
    const loadAllSubdeckStats = useCallback(async (list: Subdeck[]) => {
        if (statsAbortControllerRef.current) statsAbortControllerRef.current.abort();
        statsAbortControllerRef.current = new AbortController();
        const signal = statsAbortControllerRef.current.signal;

        const flashcards = list.filter(s => s.type !== 'apostila');
        if (!flashcards.length) return;

        const statsMap = new Map<string, SubdeckStats>();

        flashcards.forEach(s => {
            const raw = sessionStorage.getItem(`displayed_mastery_${deckId}_${s._id}`);
            if (!raw) return;
            try {
                const parsed = JSON.parse(raw);
                if (typeof parsed === 'object' && parsed.mastery !== undefined) {
                    const age = Date.now() - (parsed.savedAt || 0);
                    if (age < 2 * 60 * 60 * 1000) {
                        statsMap.set(s._id, { totalCards: s.cardCount || 0, masteredCards: 0, masteryPercentage: parsed.mastery });
                    }
                }
            } catch { /* ignore */ }
        });
        if (statsMap.size > 0) setSubdeckStats(new Map(statsMap));

        const BATCH = flashcards.length <= 15 ? flashcards.length : 5;
        for (let i = 0; i < flashcards.length; i += BATCH) {
            const batch = flashcards.slice(i, i + BATCH);
            const results = await Promise.all(batch.map(async s => {
                try {
                    const r = await fetch(`/api/progress/stats/${deckId}/${s._id}`, {
                        headers: { Authorization: `Bearer ${token()}` }, signal
                    });
                    if (!r.ok) return null;
                    const data = await r.json();
                    return {
                        id: s._id,
                        name: s.name,
                        stats: {
                            totalCards: data.totalCards,
                            masteredCards: data.masteredCards,
                            masteryPercentage: data.masteryPercentage,
                            lastReviewedAt: data.lastReviewedAt || null
                        }
                    };
                } catch (e: any) {
                    if (e.name === 'AbortError') return null;
                    return null;
                }
            }));
            results.forEach(r => {
                if (!r) return;
                if (!statsMap.has(r.id)) statsMap.set(r.id, r.stats);
                else {
                    const existing = statsMap.get(r.id)!;
                    statsMap.set(r.id, { ...r.stats, masteryPercentage: existing.masteryPercentage });
                }
            });
            setSubdeckStats(new Map(statsMap));

            // Atualizar lastStudiedSubdeck após cada batch
            let bestTime: string | null = null;
            let bestEntry: LastStudiedSubdeck | null = null;
            statsMap.forEach((stats, sid) => {
                if (stats.lastReviewedAt) {
                    if (!bestTime || stats.lastReviewedAt > bestTime) {
                        bestTime = stats.lastReviewedAt;
                        const sub = list.find(s => s._id === sid);
                        if (sub) {
                            bestEntry = {
                                subdeckId: sid,
                                subdeckName: sub.name,
                                lastStudiedAt: stats.lastReviewedAt,
                                masteryPercentage: stats.masteryPercentage
                            };
                        }
                    }
                }
            });
            if (bestEntry) setLastStudiedSubdeck(bestEntry);
        }
    }, [deckId]);

    const loadReadStatus = useCallback(async (list: Subdeck[]) => {
        const apostilas = list.filter(s => s.type === 'apostila' && s.hasResumo);
        if (!apostilas.length) return;
        try {
            const ids = apostilas.map(s => s._id).join(',');
            const r = await fetch(`/api/decks/${deckId}/subdecks-resumo-meta?ids=${ids}`, {
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (!r.ok) return;
            const data = await r.json();
            const readMap = new Map<string, boolean>();
            const pagesMap = new Map<string, number>();
            const pctMap = new Map<string, number>();
            for (const [sid, m] of Object.entries(data.meta as Record<string, { hasRead: boolean; pageCount: number; readPercentage?: number }>)) {
                readMap.set(sid, m.hasRead);
                if (m.pageCount > 0) pagesMap.set(sid, m.pageCount);
                if (m.readPercentage !== undefined) pctMap.set(sid, m.readPercentage);
            }
            setReadStatusMap(new Map(readMap));
            setPageCountMap(new Map(pagesMap));
            setReadPercentageMap(new Map(pctMap));
        } catch { /* ignore */ }
    }, [deckId]);

    // ── Load groups ───────────────────────────────────────────────────
    const loadGroups = useCallback(async () => {
        setGroupsLoading(true);
        try {
            const r = await fetch(`/api/decks/${deckId}/groups`, {
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (r.ok) {
                const data = await r.json();
                setGroups(data);
            }
        } catch { /* ignore */ }
        finally { setGroupsLoading(false); }
    }, [deckId]);

    // ── Load subdecks ─────────────────────────────────────────────────
    const loadSubdecks = useCallback(async () => {
        const cached = sessionStorage.getItem(`subdecks_${deckId}`);
        if (cached) {
            const p = JSON.parse(cached);
            if (Date.now() - p.timestamp < 600000) {
                setDeckName(p.deckName);
                setDeckDescription(p.deckDescription);
                setSubdecks(p.subdecks);
                setTotalCards(p.totalCards);
                if (p.layoutMode) setLayoutMode(p.layoutMode);
                if (p.headerTheme) { setHeaderTheme(p.headerTheme); setSelectedTheme(p.headerTheme); }
                if (p.deckIconId !== undefined) setDeckIconId(p.deckIconId);
                if (p.skipDeckLevel !== undefined) setIsSkipDeckLevel(p.skipDeckLevel || context.skipDeckLevel === true);
                setLoading(false);
                loadAllSubdeckStats(p.subdecks);
                loadReadStatus(p.subdecks);
                return;
            }
        }
        try {
            setLoading(true);
            const r = await fetch(`/api/decks/${deckId}/subdecks-meta`, {
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (r.ok) {
                const data = await r.json();
                const list = (data.subdecks || []).map((s: Subdeck) => ({ ...s, hasResumo: s.hasResumo || false }));
                setDeckName(data.deckName);
                setDeckDescription(data.deckDescription || '');
                setSubdecks(list);
                setTotalCards(data.totalCards || 0);
                if (data.layoutMode) setLayoutMode(data.layoutMode);
                if (data.headerTheme) { setHeaderTheme(data.headerTheme); setSelectedTheme(data.headerTheme); }
                setDeckIconId(data.deckIconId || null);
                setIsSkipDeckLevel(data.skipDeckLevel || context.skipDeckLevel === true);
                setLoading(false);
                sessionStorage.setItem(`subdecks_${deckId}`, JSON.stringify({
                    deckName: data.deckName, deckDescription: data.deckDescription || '',
                    subdecks: list, totalCards: data.totalCards || 0,
                    layoutMode: data.layoutMode || 'groups',
                    headerTheme: data.headerTheme || 'default',
                    deckIconId: data.deckIconId || null,
                    skipDeckLevel: data.skipDeckLevel || false,
                    timestamp: Date.now()
                }));
                loadAllSubdeckStats(list);
                loadReadStatus(list);
                setTimeout(() => list.forEach((s: Subdeck) => prefetchModeStatsRef.current(s)), 100);
                setTimeout(() => prefetchCustomStudyRef.current(), 200);
            } else {
                setToast({ message: 'Erro ao carregar subdecks', type: 'error' });
            }
        } catch {
            setToast({ message: 'Erro ao carregar subdecks', type: 'error' });
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deckId, loadAllSubdeckStats, loadReadStatus]);

    useEffect(() => {
        loadSubdecks();
        loadGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deckId]);

    useEffect(() => {
        const state = location.state as any;
        if (state?.refreshSubdeck) loadSubdecks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    // Scroll to subdeck if coming from search
    useEffect(() => {
        const state = location.state as any;
        if (state?.scrollToSubdeck) {
            setTimeout(() => {
                const el = document.getElementById(`subdeck-${state.scrollToSubdeck}`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.style.boxShadow = '0 0 0 4px #2a4a5a';
                    setTimeout(() => { el.style.boxShadow = ''; }, 2000);
                }
            }, 500);
        }
    }, [location.state]);

    // Listen for resumo deleted event
    useEffect(() => {
        const handler = (e: Event) => {
            const { subdeckId } = (e as CustomEvent).detail;
            setSubdecks(prev => prev.map(s => s._id === subdeckId ? { ...s, hasResumo: false } : s));
            setTimeout(() => loadSubdecks(), 800);
        };
        window.addEventListener('resumo-deleted', handler);
        return () => window.removeEventListener('resumo-deleted', handler);
    }, [loadSubdecks]);

    // ── Prefetch helpers ───────────────────────────────────────────────
    const prefetchModeStats = useCallback(async (s: Subdeck) => {
        if (s.type === 'apostila' || modeStatsCacheRef.current.has(s._id)) return;
        try {
            const r = await fetch(`/api/progress/mode-stats/${deckId}/${s._id}`, {
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (r.ok) {
                const data = await r.json();
                modeStatsCacheRef.current.set(s._id, data);
                setModeStatsCache(prev => new Map(prev).set(s._id, data));
            }
        } catch { /* ignore */ }
    }, [deckId]);
    prefetchModeStatsRef.current = prefetchModeStats;

    const prefetchCustomStudyItems = useCallback(async () => {
        if (customStudyCache !== null) return;
        try {
            const r = await fetch(`/api/decks/${deckId}/subdecks-meta`, {
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (r.ok) {
                const data = await r.json();
                setCustomStudyCache((data.subdecks || []).filter((s: any) => s.type !== 'apostila').map((s: any) => ({
                    _id: s._id, name: s.name, description: s.description || '', cardCount: s.cardCount || 0,
                    groupId: s.groupId || null,
                })));
            }
        } catch { /* ignore */ }
    }, [customStudyCache, deckId]);
    prefetchCustomStudyRef.current = prefetchCustomStudyItems;

    const prefetchSubdeckPageData = useCallback(async (s: Subdeck) => {
        if (pageDataCacheRef.current.has(s._id)) return;
        try {
            const r = await fetch(`/api/decks/${deckId}/subdecks/${s._id}`, {
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (r.ok) pageDataCacheRef.current.set(s._id, await r.json());
        } catch { /* ignore */ }
    }, [deckId]);

    const handleToggleFree = async (subdeckId: string) => {
        try {
            const res = await fetch(`/api/decks/${deckId}/subdecks/${subdeckId}/toggle-free`, {
                method: 'PATCH', headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSubdecks(prev => prev.map(s => s._id === subdeckId ? { ...s, isFree: data.isFree } : s));
                setToast({ message: data.message, type: 'success' });
                clearCache();
            }
        } catch { setToast({ message: 'Erro ao alternar gratuidade', type: 'error' }); }
    };

    // ── CRUD actions ───────────────────────────────────────────────────
    const handleCreate = async (name: string, description: string, type: 'flashcards' | 'apostila', groupId?: string | null) => {
        const r = await fetch(`/api/decks/${deckId}/subdecks`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
            body: JSON.stringify({ name, description, type, groupId: groupId || null })
        });
        if (r.ok) {
            clearCache();
            await Promise.all([loadSubdecks(), loadGroups()]);
            setShowCreateModal(false);
            setToast({ message: 'Subdeck criado!', type: 'success' });
        } else {
            const e = await r.json();
            setToast({ message: e.message || 'Erro ao criar', type: 'error' });
        }
    };

    const handleOpenCreate = (type?: 'flashcards' | 'apostila') => {
        if (type) setCreateType(type);
        setShowCreateModal(true);
    };

    const confirmDelete = async () => {
        if (!subdeckToDelete) return;
        const r = await fetch(`/api/decks/${deckId}/subdecks/${subdeckToDelete}`, {
            method: 'DELETE', headers: { Authorization: `Bearer ${token()}` }
        });
        if (r.ok) {
            setShowDeleteDialog(false); setSubdeckToDelete(null);
            clearCache(); loadSubdecks();
            setToast({ message: 'Subdeck excluído!', type: 'success' });
        } else {
            setToast({ message: 'Erro ao excluir', type: 'error' });
        }
    };

    const handleToggleBlock = async (subdeckId: string) => {
        const r = await fetch(`/api/decks/${deckId}/subdecks/${subdeckId}/toggle-block`, {
            method: 'PATCH', headers: { Authorization: `Bearer ${token()}` }
        });
        if (r.ok) {
            const data = await r.json();
            setSubdecks(prev => prev.map(s => s._id === subdeckId ? { ...s, isBlocked: data.subdeck.isBlocked } : s));
            setToast({ message: data.message, type: 'success' });
            clearCache();
        }
    };

    const handleToggleIntegrarApostila = async (subdeckId: string) => {
        const subdeck = subdecks.find(s => s._id === subdeckId);
        if (!subdeck) return;
        if (subdeck.hasResumo) {
            await fetch(`/api/decks/${deckId}/subdecks/${subdeckId}/resumo`, {
                method: 'DELETE', headers: { Authorization: `Bearer ${token()}` }
            });
            setSubdecks(prev => prev.map(s => s._id === subdeckId ? { ...s, hasResumo: false } : s));
            setToast({ message: 'Apostila removida do subdeck', type: 'info' });
        } else {
            setSubdecks(prev => prev.map(s => s._id === subdeckId ? { ...s, hasResumo: true } : s));
            setToast({ message: 'Botão de apostila ativado. Acesse para criar o conteúdo.', type: 'info' });
        }
        clearCache();
    };

    const handleMarkRead = async (subdeckId: string) => {
        const r = await fetch(`/api/decks/${deckId}/subdecks/${subdeckId}/resumo/mark-read`, {
            method: 'POST', headers: { Authorization: `Bearer ${token()}` }
        });
        if (r.ok) {
            const data = await r.json();
            setReadStatusMap(prev => new Map(prev).set(subdeckId, data.hasRead));
            setToast({ message: data.message, type: 'success' });
        }
    };

    const handleExport = async (subdeckId: string) => {
        try {
            await exportImportService.exportSubdeck(deckId!, subdeckId, false);
            setToast({ message: 'Subdeck exportado!', type: 'success' });
        } catch { setToast({ message: 'Erro ao exportar', type: 'error' }); }
    };

    const handleImport = async () => {
        if (!importFiles.length) return;
        try {
            setImporting(true);
            let successCount = 0;
            const failed: string[] = [];
            for (const file of importFiles) {
                try {
                    // Com vários arquivos, o progresso indica qual deles está sendo processado
                    const onProgress = importFiles.length > 1
                        ? (msg: string) => setImportProgress(`${file.name}: ${msg}`)
                        : setImportProgress;
                    await exportImportService.importSubdeck(file, deckId!, false, importGroupId || undefined, onProgress);
                    successCount++;
                } catch (e: any) {
                    failed.push(e?.message ? `${file.name} (${e.message})` : file.name);
                }
            }
            setShowImportDialog(false); setImportFiles([]); setImportGroupId(null);
            clearCache(); loadSubdecks();
            if (failed.length === 0) {
                setToast({
                    message: successCount > 1 ? `${successCount} subdecks importados!` : 'Subdeck importado!',
                    type: 'success'
                });
            } else {
                setToast({
                    message: `${successCount} importado(s), ${failed.length} falhou(aram): ${failed.join(', ')}`,
                    type: successCount > 0 ? 'info' : 'error'
                });
            }
        } finally { setImporting(false); setImportProgress(''); }
    };

    const handleImportApostila = async (importedDataList: any[]) => {
        let successCount = 0;
        const failed: string[] = [];

        for (const importedData of importedDataList) {
            const name = importedData.title || importedData.__subdeckName || 'Nova Apostila';
            try {
                const r = await fetch(`/api/decks/${deckId}/subdecks`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                    body: JSON.stringify({ name, description: '', type: 'apostila' })
                });
                if (!r.ok) {
                    const e = await r.json();
                    throw new Error(e.message || 'Erro ao criar apostila');
                }
                const created = await r.json();
                const newSubdeckId = created._id || created.subdeck?._id;
                if (!newSubdeckId) throw new Error('ID do subdeck não retornado');

                await resumoService.saveResumo(deckId!, newSubdeckId, {
                    title: importedData.title || name,
                    blocks: importedData.blocks || [],
                    pages: importedData.pages || [],
                    quizzes: importedData.quizzes || {},
                    metadata: {
                        subdeckId: newSubdeckId,
                        subdeckName: name,
                        deckId: deckId!,
                        deckName,
                        lastEdited: new Date().toISOString(),
                        version: 1
                    }
                } as any, false);
                successCount++;
            } catch (e: any) {
                failed.push(name);
            }
        }

        setShowImportApostilaModal(false);
        clearCache();
        await loadSubdecks();

        if (failed.length === 0) {
            setToast({
                message: successCount > 1 ? `${successCount} apostilas importadas!` : 'Apostila importada com sucesso!',
                type: 'success'
            });
        } else {
            setToast({
                message: `${successCount} importada(s), ${failed.length} falhou(aram): ${failed.join(', ')}`,
                type: successCount > 0 ? 'info' : 'error'
            });
        }
    };

    const handleSaveLayout = async (mode: 'groups' | 'grid') => {
        try {
            setSavingLayout(true);
            const r = await fetch(`/api/decks/${deckId}/layout`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                body: JSON.stringify({ layoutMode: mode })
            });
            if (r.ok) {
                setLayoutMode(mode);
                // Atualiza cache
                const cached = sessionStorage.getItem(`subdecks_${deckId}`);
                if (cached) {
                    const p = JSON.parse(cached);
                    sessionStorage.setItem(`subdecks_${deckId}`, JSON.stringify({ ...p, layoutMode: mode }));
                }
                setShowLayoutModal(false);
                setToast({ message: 'Layout atualizado!', type: 'success' });
            }
        } catch { setToast({ message: 'Erro ao salvar layout', type: 'error' }); }
        finally { setSavingLayout(false); }
    };

    const handleStudyClick = (subdeck: Subdeck) => {
        if (subdeck.isBlocked && !isAdmin && !isEditor) {
            setSelectedSubdeckForModal(subdeck.name);
            setShowInterdictedModal(true);
            return;
        }
        if (!isAdmin && !isPurchasedDeck && !subdeck.freeConfig?.enabled) {
            setShowUpgradeModal(true);
            return;
        }
        sessionStorage.setItem('graia_theme_context', 'enem');
        setSelectedSubdeckForStudy(subdeck);
        setShowModeModal(true);
    };

    const handleView = (subdeck: Subdeck) => {
        if (subdeck.isBlocked && !isAdmin && !isEditor) {
            setSelectedSubdeckForModal(subdeck.name);
            setShowInterdictedModal(true);
            return;
        }
        if (!isAdmin && !isPurchasedDeck) { setShowUpgradeModal(true); return; }
        sessionStorage.setItem('graia_theme_context', 'enem');
        navigate(`/view/${deckId}/${subdeck._id}`, {
            state: { ...context, fromSubdecks: true, prefetchedData: pageDataCacheRef.current.get(subdeck._id) || null, subdecksPath: `/subdecks/${deckId}` }
        });
    };

    const handleEdit = (subdeck: Subdeck) => {
        if (statsAbortControllerRef.current) statsAbortControllerRef.current.abort();
        if (!isAdmin && !isEditor && subdeck.isBlocked) {
            setSelectedSubdeckForModal(subdeck.name);
            setShowInterdictedModal(true);
            return;
        }
        sessionStorage.setItem('graia_theme_context', 'enem');
        navigate(`/editor/${deckId}/${subdeck._id}`, {
            state: { ...context, fromSubdecks: true, subdecksPath: `/subdecks/${deckId}` }
        });
    };


    const gridSensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    // Grid mode drag & drop
    const handleGridDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const sorted = [...subdecks].sort((a, b) => (a as any).order - (b as any).order);
        const oldIndex = sorted.findIndex(s => s._id === active.id);
        const newIndex = sorted.findIndex(s => s._id === over.id);
        const reordered = arrayMove(sorted, oldIndex, newIndex);
        const reorderedWithOrder = reordered.map((s, i) => ({ ...s, order: i }));
        setSubdecks(reorderedWithOrder);
        try {
            await fetch(`/api/decks/${deckId}/subdecks/reorder`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                body: JSON.stringify({ subdecks: reorderedWithOrder.map((s, i) => ({ _id: s._id, order: i })) })
            });
            clearCache();
            setToast({ message: 'Nova ordem salva!', type: 'success' });
        } catch {
            setToast({ message: 'Erro ao salvar ordem', type: 'error' });
        }
    };

    // Grouped mode drag & drop (dentro de cada grupo)
    const handleGroupReorder = async (groupSubdecks: Subdeck[], _groupId: string | null) => {
        const inGroupIds = new Set(groupSubdecks.map(s => s._id));
        setSubdecks(prev => {
            const others = prev.filter(s => !inGroupIds.has(s._id));
            return [...groupSubdecks, ...others];
        });
        // Usa a versão atual do estado para enviar ao backend
        const others = subdecks.filter(s => !inGroupIds.has(s._id));
        const merged = [...groupSubdecks, ...others];
        try {
            await fetch(`/api/decks/${deckId}/subdecks/reorder`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                body: JSON.stringify({ subdecks: merged.map((s, i) => ({ _id: s._id, order: i })) })
            });
            clearCache();
        } catch {
            setToast({ message: 'Erro ao salvar ordem', type: 'error' });
        }
    };

    const handleGoBack = () => {
        if (context.returnState) {
            navigate(returnPath, { state: context.returnState });
        } else {
            navigate(returnPath);
        }
        sessionStorage.removeItem('subdecksContext');
    };

    const handleThemeSave = async () => {
        setThemeSaving(true);
        try {
            // Find the parent cycleId from context
            const cycleId = context.cycleId || context.from;
            if (cycleId) {
                await fetch(`/api/cycles/${cycleId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                    body: JSON.stringify({ headerTheme: selectedTheme })
                });
            }
            setHeaderTheme(selectedTheme);
            // Atualizar cache
            const cached = sessionStorage.getItem(`subdecks_${deckId}`);
            if (cached) {
                const p = JSON.parse(cached);
                sessionStorage.setItem(`subdecks_${deckId}`, JSON.stringify({ ...p, headerTheme: selectedTheme }));
            }
        } catch { /* ignore */ }
        setThemeSaving(false);
        setShowThemeModal(false);
    };

    // Click-outside para fechar admin menu
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (adminMenuRef.current && !adminMenuRef.current.contains(e.target as Node)) {
                setShowAdminMenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Subdecks filtrados por busca
    const filteredSubdecks = searchQuery.trim()
        ? subdecks.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()))
        : subdecks;

    // Top 3 por domínio
    const top3 = [...subdecks]
        .filter(s => s.type !== 'apostila' && (subdeckStats.get(s._id)?.masteryPercentage || 0) > 0)
        .sort((a, b) => (subdeckStats.get(b._id)?.masteryPercentage || 0) - (subdeckStats.get(a._id)?.masteryPercentage || 0))
        .slice(0, 3);

    // Todos os flashcard subdecks ranqueados por domínio (para o modal)
    const allRanked = [...subdecks]
        .filter(s => s.type !== 'apostila')
        .map(s => ({ ...s, pct: subdeckStats.get(s._id)?.masteryPercentage ?? -1 }))
        .sort((a, b) => b.pct - a.pct);

    const hasFlashcardSubdecks = subdecks.some(s => s.type !== 'apostila');
    const statsLoading = hasFlashcardSubdecks && subdeckStats.size === 0;

    const HeroIconComp = deckIconId ? ICON_MAP[deckIconId] : null;

    // ── Group actions ─────────────────────────────────────────────────
    const handleGroupsChange = (updated: SubdeckGroup[]) => {
        setGroups(updated);
        sessionStorage.removeItem(`groups_${deckId}`);
    };

    const handleAssignGroupSuccess = (subdeckId: string, groupId: string | null) => {
        setSubdecks(prev => prev.map(s => s._id === subdeckId ? { ...s, groupId } : s));
        clearCache();
        // Recarrega grupos para atualizar contagens
        loadGroups();
    };

    // ── Shared SubdeckCard props builder ──────────────────────────────
    const buildCardProps = (subdeck: Subdeck) => {
        const stats = subdeckStats.get(subdeck._id);
        const mastery = stats && stats.totalCards > 0 ? stats.masteryPercentage : -1;
        const isLocked = !isAdmin && !isPurchasedDeck && !subdeck.freeConfig?.enabled;
        return {
            subdeck,
            isAdmin,
            canManage,
            masteryPercentage: mastery,
            deckId: deckId!,
            deckName,
            deckIconId,
            context,
            isLockedForUser: isLocked,
            isPurchasedDeck,
            hasRead: readStatusMap.get(subdeck._id),
            readPercentage: readPercentageMap.get(subdeck._id),
            onStudy: () => handleStudyClick(subdeck),
            onView: () => handleView(subdeck),
            onEdit: () => handleEdit(subdeck),
            onExport: () => handleExport(subdeck._id),
            onMove: () => { setSubdeckToMove(subdeck); setShowMoveModal(true); },
            onCopy: isAdmin ? () => { setSubdeckToCopy(subdeck); setShowCopyModal(true); } : undefined,
            onDelete: () => { setSubdeckToDelete(subdeck._id); setShowDeleteDialog(true); },
            onToggleBlock: () => handleToggleBlock(subdeck._id),
            onToggleFree: () => handleToggleFree(subdeck._id),
            onToggleIntegrarApostila: () => handleToggleIntegrarApostila(subdeck._id),
            onMarkRead: () => handleMarkRead(subdeck._id),
            onShowNoResumoModal: (n: string) => { setShowNoResumoModal(true); setSelectedSubdeckForModal(n); },
            onPrefetch: () => prefetchModeStats(subdeck),
            onPrefetchPageData: () => prefetchSubdeckPageData(subdeck),
            onRename: async (name: string, desc: string, colorId?: string) => {
                await fetch(`/api/decks/${deckId}/subdecks/${subdeck._id}/meta`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                    body: JSON.stringify({ name, description: desc, colorId })
                });
                clearCache();
                await loadSubdecks();
                setToast({ message: 'Subdeck atualizado!', type: 'success' });
            },
            onAssignGroup: () => { setSubdeckForGroup(subdeck); setShowAssignGroupModal(true); },
        };
    };

    // ── Grid renderer (layout flat) ───────────────────────────────────
    const renderGrid = (list?: Subdeck[]) => {
        const sorted = [...(list ?? subdecks)].sort((a, b) => (a as any).order - (b as any).order);
        if (sorted.length === 0) {
            return (
                <div className="sdp-empty-state">
                    <h2>Nenhum subdeck ainda</h2>
                    <p>{isAdmin ? 'Crie o primeiro subdeck pelo botão de configurações acima.' : 'Este deck ainda não possui subdecks.'}</p>
                </div>
            );
        }
        return (
            <DndContext
                sensors={gridSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleGridDragEnd}
            >
                <SortableContext items={sorted.map(s => s._id)} strategy={rectSortingStrategy}>
                    <div className="sdp-grid">
                        {sorted.map(subdeck => {
                            const stats = subdeckStats.get(subdeck._id);
                            const mastery = stats && stats.totalCards > 0 ? stats.masteryPercentage : -1;
                            const isLocked = !isAdmin && !isPurchasedDeck && !subdeck.freeConfig?.enabled;
                            const props = buildCardProps(subdeck);
                            return (
                                <SubdeckCard
                                    key={subdeck._id}
                                    subdeck={subdeck}
                                    isAdmin={isAdmin}
                                    isEditor={isEditor}
                                    canManage={canManage}
                                    masteryPercentage={mastery}
                                    deckId={deckId!}
                                    deckName={deckName}
                                    deckIconId={deckIconId}
                                    context={context}
                                    isLockedForUser={isLocked}
                                    isPurchasedDeck={isPurchasedDeck}
                                    hasRead={readStatusMap.get(subdeck._id)}
                                    readPercentage={readPercentageMap.get(subdeck._id)}
                                    pageCount={pageCountMap.get(subdeck._id)}
                                    isGridMode={true}
                                    onStudy={() => handleStudyClick(subdeck)}
                                    onView={() => handleView(subdeck)}
                                    onEdit={() => handleEdit(subdeck)}
                                    onExport={() => handleExport(subdeck._id)}
                                    onMove={() => { setSubdeckToMove(subdeck); setShowMoveModal(true); }}
                                    onCopy={isAdmin ? () => { setSubdeckToCopy(subdeck); setShowCopyModal(true); } : undefined}
                                    onDelete={() => { setSubdeckToDelete(subdeck._id); setShowDeleteDialog(true); }}
                                    onToggleBlock={() => handleToggleBlock(subdeck._id)}
                                    onToggleFree={() => handleToggleFree(subdeck._id)}
                                    onToggleIntegrarApostila={() => handleToggleIntegrarApostila(subdeck._id)}
                                    onMarkRead={() => handleMarkRead(subdeck._id)}
                                    onShowNoResumoModal={(n) => { setShowNoResumoModal(true); setSelectedSubdeckForModal(n); }}
                                    onPrefetch={() => prefetchModeStats(subdeck)}
                                    onPrefetchPageData={() => prefetchSubdeckPageData(subdeck)}
                                    onRename={props.onRename}
                                    onAssignGroup={() => { setSubdeckForGroup(subdeck); setShowAssignGroupModal(true); }}
                                />
                            );
                        })}
                    </div>
                </SortableContext>
            </DndContext>
        );
    };

    // ── Grouped renderer ──────────────────────────────────────────────
    const renderGrouped = () => {
        const sortedGroups = [...groups].sort((a, b) => a.order - b.order);
        const ungrouped = subdecks.filter(s => !s.groupId);

        return (
            <div className="sdp-groups-list">
                {sortedGroups.map(group => {
                    const groupSubdecks = subdecks.filter(s => s.groupId === group._id);
                    const groupFlashcards = groupSubdecks.filter(s => s.type !== 'apostila');
                    return (
                        <SubdeckGroupContainer
                            key={group._id}
                            group={group}
                            subdecks={groupSubdecks}
                            isExpanded={openGroups.has(group._id)}
                            onToggle={() => toggleGroup(group._id)}
                            isAdmin={isAdmin}
                            isEditor={isEditor}
                            canManage={canManage}
                            onStudyGroup={groupFlashcards.length > 0 ? () => {
                                setGroupStudyItems(groupFlashcards.map(s => ({
                                    _id: s._id, name: s.name,
                                    description: s.description || '',
                                    cardCount: s.cardCount || 0,
                                })));
                                setGroupStudyName(group.name);
                                setShowGroupStudyModal(true);
                            } : undefined}
                            onEditGroup={() => {
                                // Abre o ManageGroupsModal já em modo edição via o modal completo
                                setShowManageGroupsModal(true);
                            }}
                            onDeleteGroup={async () => {
                                if (!window.confirm(`Excluir o grupo "${group.name}"? Os subdecks ficarão sem grupo.`)) return;
                                try {
                                    const r = await fetch(`/api/decks/${deckId}/groups/${group._id}`, {
                                        method: 'DELETE', headers: { Authorization: `Bearer ${token()}` }
                                    });
                                    if (r.ok) {
                                        setGroups(prev => prev.filter(g => g._id !== group._id));
                                        setSubdecks(prev => prev.map(s => s.groupId === group._id ? { ...s, groupId: null } : s));
                                        setToast({ message: 'Grupo excluído.', type: 'success' });
                                        clearCache();
                                    }
                                } catch { setToast({ message: 'Erro ao excluir grupo', type: 'error' }); }
                            }}
                            onReorderSubdecks={(reordered) => handleGroupReorder(reordered, group._id)}
                            deckId={deckId!}
                            deckName={deckName}
                            deckIconId={deckIconId}
                            context={context}
                            isPurchasedDeck={isPurchasedDeck}
                            subdeckStats={subdeckStats}
                            readStatusMap={readStatusMap}
                            readPercentageMap={readPercentageMap}
                            pageCountMap={pageCountMap}
                            modeStatsCache={modeStatsCache}
                            onStudy={handleStudyClick}
                            onView={handleView}
                            onEdit={handleEdit}
                            onExport={handleExport}
                            onMove={(s) => { setSubdeckToMove(s); setShowMoveModal(true); }}
                            onCopy={isAdmin ? (s) => { setSubdeckToCopy(s); setShowCopyModal(true); } : undefined}
                            onDelete={(id) => { setSubdeckToDelete(id); setShowDeleteDialog(true); }}
                            onToggleBlock={handleToggleBlock}
                            onToggleFree={handleToggleFree}
                            onToggleIntegrarApostila={handleToggleIntegrarApostila}
                            onMarkRead={handleMarkRead}
                            onRename={(id, name, desc, colorId) => buildCardProps(subdecks.find(s => s._id === id)!).onRename(name, desc, colorId)}
                            onShowNoResumoModal={(n) => { setShowNoResumoModal(true); setSelectedSubdeckForModal(n); }}
                            onPrefetch={prefetchModeStats}
                            onPrefetchPageData={prefetchSubdeckPageData}
                            onAssignGroup={(s) => { setSubdeckForGroup(s); setShowAssignGroupModal(true); }}
                        />
                    );
                })}

                {/* Container "Sem grupo" */}
                {ungrouped.length > 0 && (
                    <SubdeckGroupContainer
                        group={null}
                        subdecks={ungrouped}
                        isExpanded={openGroups.has('__ungrouped__')}
                        onToggle={() => toggleGroup('__ungrouped__')}
                        isAdmin={isAdmin}
                        isEditor={isEditor}
                        canManage={canManage}
                        onStudyGroup={ungrouped.some(s => s.type !== 'apostila') ? () => {
                            const flashcards = ungrouped.filter(s => s.type !== 'apostila');
                            setGroupStudyItems(flashcards.map(s => ({
                                _id: s._id, name: s.name,
                                description: s.description || '',
                                cardCount: s.cardCount || 0,
                            })));
                            setGroupStudyName('Sem grupo');
                            setShowGroupStudyModal(true);
                        } : undefined}
                        onReorderSubdecks={(reordered) => handleGroupReorder(reordered, null)}
                        deckId={deckId!}
                        deckName={deckName}
                        context={context}
                        isPurchasedDeck={isPurchasedDeck}
                        subdeckStats={subdeckStats}
                        readStatusMap={readStatusMap}
                        readPercentageMap={readPercentageMap}
                        pageCountMap={pageCountMap}
                        modeStatsCache={modeStatsCache}
                        onStudy={handleStudyClick}
                        onView={handleView}
                        onEdit={handleEdit}
                        onExport={handleExport}
                        onMove={(s) => { setSubdeckToMove(s); setShowMoveModal(true); }}
                        onCopy={isAdmin ? (s) => { setSubdeckToCopy(s); setShowCopyModal(true); } : undefined}
                        onDelete={(id) => { setSubdeckToDelete(id); setShowDeleteDialog(true); }}
                        onToggleBlock={handleToggleBlock}
                        onToggleFree={handleToggleFree}
                        onToggleIntegrarApostila={handleToggleIntegrarApostila}
                        onMarkRead={handleMarkRead}
                        onRename={(id, name, desc) => buildCardProps(subdecks.find(s => s._id === id)!).onRename(name, desc)}
                        onShowNoResumoModal={(n) => { setShowNoResumoModal(true); setSelectedSubdeckForModal(n); }}
                        onPrefetch={prefetchModeStats}
                        onPrefetchPageData={prefetchSubdeckPageData}
                        onAssignGroup={(s) => { setSubdeckForGroup(s); setShowAssignGroupModal(true); }}
                    />
                )}

                {/* Se não há grupos e não há ungrouped */}
                {sortedGroups.length === 0 && ungrouped.length === 0 && (
                    <div className="sdp-empty-state">
                        <h2>Nenhum subdeck ainda</h2>
                        <p>{isAdmin ? 'Crie o primeiro subdeck pelo botão de configurações acima.' : 'Este deck ainda não possui subdecks.'}</p>
                    </div>
                )}
            </div>
        );
    };


    // ── Render ─────────────────────────────────────────────────────────
    if (loading || groupsLoading) return (
        <div className="sdp-page">
            <div className="sdp-loading">Carregando...</div>
        </div>
    );

    return (
        <div className="sdp-page">

            {/* ══ HERO HEADER ══ */}
            <div className="sdp-hero-header">
                <div className="sdp-hero-deco-clip">
                    <HeaderPattern theme={headerTheme} />
                </div>

                {/* Linha 1: [←] [Título centro] [Estudo Personalizado] */}
                <div className="sdp-hero-top-row">
                    <button className="sdp-btn-back" onClick={handleGoBack} title="Voltar">
                        <ArrowLeft size={18} />
                    </button>

                    <div className="sdp-hero-meta">
                        <h1 className="sdp-hero-title">{deckName}</h1>
                        <p className="sdp-hero-subtitle">
                            {subdecks.length} subdeck{subdecks.length !== 1 ? 's' : ''}
                            {' · '}{totalCards} cards
                        </p>
                    </div>

                    <div className="sdp-hero-actions">
                        <button
                            className="sdp-study-widget"
                            onClick={() => setShowCustomStudyModal(true)}
                        >
                            <div className="sdp-study-widget-icon">
                                <GraduationCap size={16} />
                            </div>
                            <div className="sdp-study-widget-text">
                                <span className="sdp-study-widget-title">Estudo Personalizado</span>
                                <span className="sdp-study-widget-sub">Montar sessão</span>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Linha 2: busca + gear admin */}
                <div className="sdp-hero-search-row">
                    <div className="sdp-hero-search">
                        <Search size={14} />
                        <input
                            className="sdp-hero-search-input"
                            placeholder="Pesquisar subdecks..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button className="sdp-hero-search-clear" onClick={() => setSearchQuery('')}>
                                <X size={12} />
                            </button>
                        )}
                    </div>

                    {isAdmin && (
                        <div className="sdp-admin-menu-wrap" ref={adminMenuRef}>
                            <button
                                className={`sdp-hero-icon-btn ${showAdminMenu ? 'sdp-hero-icon-btn--active' : ''}`}
                                onClick={() => setShowAdminMenu(p => !p)}
                                title="Configurações"
                            >
                                <Settings size={17} />
                            </button>
                            {showAdminMenu && (
                                <div className="sdp-admin-dropdown">
                                    <button className="sdp-admin-drop-item" onClick={() => { setShowAdminMenu(false); handleOpenCreate('flashcards'); }}>
                                        <Play size={14} /> Criar Flashcards
                                    </button>
                                    <button className="sdp-admin-drop-item" onClick={() => { setShowAdminMenu(false); handleOpenCreate('apostila'); }}>
                                        <Play size={14} /> Criar Apostila
                                    </button>
                                    <div className="sdp-admin-drop-divider" />
                                    <button className="sdp-admin-drop-item" onClick={() => { setShowAdminMenu(false); setShowImportDialog(true); }}>
                                        <ArrowRight size={14} /> Importar Deck
                                    </button>
                                    <button className="sdp-admin-drop-item" onClick={() => { setShowAdminMenu(false); setShowImportApostilaModal(true); }}>
                                        <ArrowRight size={14} /> Importar Apostila
                                    </button>
                                    <div className="sdp-admin-drop-divider" />
                                    <button className="sdp-admin-drop-item" onClick={() => { setShowAdminMenu(false); setShowManageGroupsModal(true); }}>
                                        <Settings size={14} /> Gerenciar Grupos
                                    </button>
                                    <button className="sdp-admin-drop-item" onClick={() => { setShowAdminMenu(false); setShowLayoutModal(true); }}>
                                        <Settings size={14} /> Modo de Layout
                                    </button>
                                    {isSkipDeckLevel && (
                                        <>
                                            <div className="sdp-admin-drop-divider" />
                                            <button className="sdp-admin-drop-item" onClick={() => { setShowAdminMenu(false); setSelectedTheme(headerTheme); setShowThemeModal(true); }}>
                                                <Trophy size={14} /> Personalizar Header
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ── HERO ── continuar + top 3 (abaixo do header, acima do grid) */}
            {hasFlashcardSubdecks && !searchQuery && (
                <div className={`sdp-hero-body${statsLoading ? ' sdp-hero-body--hidden' : ''}`}>
                    {/* Bloco: Continuar de onde parou */}
                    {lastStudiedSubdeck ? (
                        <button
                            className="sdp-hero-continue"
                            onClick={() => {
                                const sub = subdecks.find(s => s._id === lastStudiedSubdeck.subdeckId);
                                if (sub) handleStudyClick(sub);
                            }}
                        >
                            <div className="sdp-hc-icon">
                                {HeroIconComp ? <HeroIconComp size={22} /> : <BookOpen size={22} />}
                            </div>
                            <div className="sdp-hc-info">
                                <span className="sdp-hc-label">Continuar de onde parou</span>
                                <span className="sdp-hc-name">{lastStudiedSubdeck.subdeckName}</span>
                                <div className="sdp-hc-bar-wrap">
                                    <div className="sdp-hc-bar-bg">
                                        <div className="sdp-hc-bar-fill" style={{ width: `${lastStudiedSubdeck.masteryPercentage}%` }} />
                                    </div>
                                    <span className="sdp-hc-pct">{lastStudiedSubdeck.masteryPercentage}%</span>
                                </div>
                            </div>
                            <ArrowRight size={16} className="sdp-hc-arrow" />
                        </button>
                    ) : (
                        <div className="sdp-hero-continue sdp-hero-continue--empty">
                            <div className="sdp-hc-icon">
                                {HeroIconComp ? <HeroIconComp size={22} /> : <BookOpen size={22} />}
                            </div>
                            <div className="sdp-hc-info">
                                <span className="sdp-hc-label">Comece agora</span>
                                <span className="sdp-hc-name">{subdecks.filter(s => s.type !== 'apostila')[0]?.name || deckName}</span>
                                <span className="sdp-hc-meta-empty">Nenhum subdeck estudado ainda</span>
                            </div>
                        </div>
                    )}

                    {/* Bloco: Top domínio */}
                    <div className="sdp-hero-top3">
                        <p className="sdp-ht3-title">
                            <Trophy size={13} /> Top domínio
                            <button
                                className="sdp-ht3-ver-todos"
                                onClick={() => setShowRankingModal(true)}
                                title="Ver ranking completo"
                            >
                                <List size={12} /> Ver todos
                            </button>
                        </p>
                        {top3.length > 0 ? (
                            <div className="sdp-ht3-list">
                                {top3.map((sub, idx) => {
                                    const pct = subdeckStats.get(sub._id)?.masteryPercentage || 0;
                                    return (
                                        <div key={sub._id} className="sdp-ht3-item">
                                            <span className="sdp-ht3-rank">#{idx + 1}</span>
                                            <span className="sdp-ht3-name">{sub.name}</span>
                                            <div className="sdp-ht3-bar">
                                                <div className="sdp-ht3-fill" style={{ width: `${pct}%` }} />
                                            </div>
                                            <span className="sdp-ht3-pct">{pct}%</span>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="sdp-ht3-empty">
                                Estude para ver seu ranking
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="sdp-container">
                {searchQuery
                    ? renderGrid(filteredSubdecks)
                    : layoutMode === 'grid' ? renderGrid() : renderGrouped()
                }
            </div>

            {/* ── Modals ── */}

            {showCreateModal && createType && (
                <CreateSubdeckModal
                    type={createType}
                    groups={groups}
                    deckId={deckId!}
                    onClose={() => { setShowCreateModal(false); setCreateType(null); }}
                    onCreate={handleCreate}
                />
            )}

            <ConfirmDialog
                isOpen={showDeleteDialog}
                title="Excluir Subdeck"
                message="Tem certeza? Todos os cards serão perdidos permanentemente."
                onConfirm={confirmDelete}
                onCancel={() => { setShowDeleteDialog(false); setSubdeckToDelete(null); }}
                confirmText="Excluir"
                cancelText="Cancelar"
            />

            <ImportModal
                isOpen={showImportDialog}
                onClose={() => { setShowImportDialog(false); setImportFiles([]); setImportGroupId(null); }}
                onImport={handleImport}
                onFilesSelect={setImportFiles}
                selectedFiles={importFiles}
                type="subdeck"
                importing={importing}
                groups={groups}
                selectedGroupId={importGroupId}
                onGroupChange={setImportGroupId}
                acceptZip
                progressLabel={importProgress}
            />

            {showImportApostilaModal && (
                <ImportApostilaModal
                    onImport={handleImportApostila}
                    onClose={() => setShowImportApostilaModal(false)}
                />
            )}

            {/* Modal de layout */}
            {showLayoutModal && (
                <div className="sdp-page sdp-dialog-overlay" onClick={() => setShowLayoutModal(false)}>
                    <div className="sdp-dialog" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
                        <div className="sdp-dialog-header">
                            <div>
                                <h2 className="sdp-dialog-title">Layout da página</h2>
                                <p className="sdp-dialog-subtitle">Escolha como os subdecks serão exibidos para todos os usuários.</p>
                            </div>
                            <button className="sdp-dialog-close" onClick={() => setShowLayoutModal(false)}>✕</button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                            {([
                                { value: 'groups', label: 'Organização por grupos', desc: 'Subdecks organizados em grupos expansíveis com cores e ícones.' },
                                { value: 'grid', label: 'Organização em grid', desc: 'Todos os subdecks exibidos em grade, sem separação por grupos.' }
                            ] as const).map(opt => (
                                <button
                                    key={opt.value}
                                    onClick={() => handleSaveLayout(opt.value)}
                                    disabled={savingLayout}
                                    style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                                        gap: 4, padding: '16px 18px', borderRadius: 12, cursor: 'pointer',
                                        border: layoutMode === opt.value ? '2px solid #0D3B0E' : '1.5px solid rgba(0,0,0,0.10)',
                                        background: layoutMode === opt.value ? 'rgba(13,59,14,0.06)' : 'transparent',
                                        textAlign: 'left', width: '100%', fontFamily: 'inherit',
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    <span style={{ fontWeight: 700, fontSize: 15, color: layoutMode === opt.value ? '#0D3B0E' : 'inherit' }}>
                                        {layoutMode === opt.value ? '✓ ' : ''}{opt.label}
                                    </span>
                                    <span style={{ fontSize: 13, color: '#6B7280' }}>{opt.desc}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <CustomStudyModal
                show={showCustomStudyModal}
                onClose={() => setShowCustomStudyModal(false)}
                mode="subdecks"
                contextId={deckId || ''}
                contextName={deckName}
                isCustomDeck={false}
                prefetchedItems={customStudyCache}
                groups={layoutMode === 'groups' ? groups : undefined}
            />

            <CustomStudyModal
                show={showGroupStudyModal}
                onClose={() => { setShowGroupStudyModal(false); setGroupStudyItems(null); }}
                mode="subdecks"
                contextId={deckId || ''}
                contextName={groupStudyName}
                isCustomDeck={false}
                prefetchedItems={groupStudyItems}
                autoSelectAll={true}
            />

            {showModeModal && selectedSubdeckForStudy && (
                <StudyModeModal
                    isOpen={showModeModal}
                    onClose={() => { setShowModeModal(false); setSelectedSubdeckForStudy(null); }}
                    deckId={deckId!}
                    subdeckId={selectedSubdeckForStudy._id}
                    deckName={deckName}
                    subdeckName={selectedSubdeckForStudy.name}
                    isCustomDeck={false}
                    isPurchasedDeck={isPurchasedDeck}
                    prefetchedStats={modeStatsCache.get(selectedSubdeckForStudy._id)}
                />
            )}

            {showMoveModal && subdeckToMove && (
                <MoveModal
                    mode="subdeck"
                    sourceDeckId={deckId}
                    subdeckId={subdeckToMove._id}
                    subdeckName={subdeckToMove.name}
                    onClose={() => { setShowMoveModal(false); setSubdeckToMove(null); }}
                    onSuccess={() => { clearCache(); loadSubdecks(); }}
                    onToast={(msg, type) => setToast({ message: msg, type })}
                />
            )}

            {showCopyModal && subdeckToCopy && (
                <CopyModal
                    mode="subdeck"
                    sourceDeckId={deckId}
                    subdeckId={subdeckToCopy._id}
                    subdeckName={subdeckToCopy.name}
                    onClose={() => { setShowCopyModal(false); setSubdeckToCopy(null); }}
                    onSuccess={() => { clearCache(); loadSubdecks(); }}
                    onToast={(msg, type) => setToast({ message: msg, type })}
                />
            )}

            {showNoResumoModal && (
                <div className="sdp-modal-overlay-simple" onClick={() => setShowNoResumoModal(false)}>
                    <div className="sdp-modal-simple" onClick={e => e.stopPropagation()}>
                        <button className="sdp-modal-close-btn" onClick={() => setShowNoResumoModal(false)}>✕</button>
                        <div className="sdp-modal-icon">🔨</div>
                        <h2>Apostila em Construção</h2>
                        <p>A apostila de "<strong>{selectedSubdeckForModal}</strong>" ainda está sendo preparada.</p>
                    </div>
                </div>
            )}

            {showInterdictedModal && (
                <InterdictedModal
                    isOpen={showInterdictedModal}
                    onClose={() => { setShowInterdictedModal(false); setSelectedSubdeckForModal(''); }}
                    entityType="subdeck"
                    entityName={selectedSubdeckForModal}
                />
            )}

            {showUpgradeModal && (
                <UpgradeModal
                    isOpen={showUpgradeModal}
                    onClose={() => setShowUpgradeModal(false)}
                    entityType="deck"
                    entityName={deckName}
                    entityId={deckId || ''}
                    cycleName=""
                />
            )}

            {showAssignGroupModal && subdeckForGroup && (
                <AssignGroupModal
                    subdeckId={subdeckForGroup._id}
                    subdeckName={subdeckForGroup.name}
                    currentGroupId={subdeckForGroup.groupId}
                    groups={groups}
                    deckId={deckId!}
                    onClose={() => { setShowAssignGroupModal(false); setSubdeckForGroup(null); }}
                    onSuccess={handleAssignGroupSuccess}
                    onToast={(msg, type) => setToast({ message: msg, type })}
                />
            )}

            {showManageGroupsModal && (
                <ManageGroupsModal
                    deckId={deckId!}
                    groups={groups}
                    onClose={() => setShowManageGroupsModal(false)}
                    onGroupsChange={(updated) => {
                        handleGroupsChange(updated);
                        // Recarrega subdecks para atualizar groupIds após exclusão de grupo
                        clearCache();
                        loadSubdecks();
                    }}
                    onToast={(msg, type) => setToast({ message: msg, type })}
                />
            )}

            {/* ── Modal Ranking Completo ── */}
            {showRankingModal && (
                <div className="sdp-ranking-overlay" onClick={() => setShowRankingModal(false)}>
                    <div className="sdp-ranking-modal" onClick={e => e.stopPropagation()}>
                        <div className="sdp-ranking-header">
                            <div className="sdp-ranking-header-left">
                                <Trophy size={18} className="sdp-ranking-trophy" />
                                <div>
                                    <h3 className="sdp-ranking-title">Ranking de Domínio</h3>
                                    <p className="sdp-ranking-subtitle">{allRanked.length} subdecks</p>
                                </div>
                            </div>
                            <button className="sdp-ranking-close" onClick={() => setShowRankingModal(false)}>✕</button>
                        </div>
                        <div className="sdp-ranking-list">
                            {allRanked.length === 0 ? (
                                <div className="sdp-ranking-empty">Nenhum subdeck de flashcards encontrado.</div>
                            ) : (
                                allRanked.map((sub, idx) => {
                                    const pct = sub.pct >= 0 ? sub.pct : 0;
                                    const hasData = sub.pct >= 0;
                                    return (
                                        <div key={sub._id} className={`sdp-ranking-item ${!hasData ? 'sdp-ranking-item--no-data' : ''}`}>
                                            <span className={`sdp-ranking-pos ${idx === 0 ? 'sdp-ranking-pos--gold' : idx === 1 ? 'sdp-ranking-pos--silver' : idx === 2 ? 'sdp-ranking-pos--bronze' : ''}`}>
                                                {idx + 1}
                                            </span>
                                            <div className="sdp-ranking-info">
                                                <span className="sdp-ranking-name">{sub.name}</span>
                                                <div className="sdp-ranking-bar-wrap">
                                                    <div className="sdp-ranking-bar-bg">
                                                        <div
                                                            className="sdp-ranking-bar-fill"
                                                            style={{ width: `${pct}%` }}
                                                        />
                                                    </div>
                                                    <span className="sdp-ranking-pct">
                                                        {hasData ? `${pct}%` : '—'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            {/* ── Modal Personalizar Header ── */}
            {showThemeModal && (
                <div className="sdp-theme-overlay" onClick={() => setShowThemeModal(false)}>
                    <div className="sdp-theme-modal" onClick={e => e.stopPropagation()}>
                        <h3>Personalizar Header</h3>
                        <p className="sdp-theme-modal-sub">Escolha o padrão visual do cabeçalho desta página.</p>
                        <div className="sdp-theme-grid">
                            {HEADER_THEMES.map(t => (
                                <button
                                    key={t.id}
                                    className={`sdp-theme-btn ${selectedTheme === t.id ? 'sdp-theme-btn--active' : ''}`}
                                    onClick={() => setSelectedTheme(t.id)}
                                >
                                    <div style={{ position: 'absolute', inset: 0, opacity: 0.8, pointerEvents: 'none' }}>
                                        <HeaderPattern theme={t.id} />
                                    </div>
                                    <span className="sdp-theme-btn-emoji" style={{ position: 'relative', zIndex: 1 }}>{t.emoji}</span>
                                    <span className="sdp-theme-btn-label" style={{ position: 'relative', zIndex: 1 }}>{t.label}</span>
                                </button>
                            ))}
                        </div>
                        <div className="sdp-theme-modal-actions">
                            <button className="sdp-theme-modal-cancel" onClick={() => setShowThemeModal(false)}>Cancelar</button>
                            <button
                                className="sdp-theme-modal-save"
                                onClick={handleThemeSave}
                                disabled={themeSaving}
                            >
                                {themeSaving ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubdecksPage;
